import {
  type AnyEvent,
  createEvent,
  type EventType,
  idempotencyKey,
  localDateOf,
  type PayloadOf,
  safeParseEvent,
} from '@disdis/shared'
import { log, reason } from '../app/log.js'
import { db, type StoredEvent } from './db.js'
import { deviceId } from './device.js'

const logger = log('events')

export type AppendOptions = {
  childId: string
  reviewMode?: boolean
  /** Tests and the fake clock pass this; production always uses the device clock. */
  now?: number
}

/**
 * The only writer of the event log. CONTRACTS §2 rule 3: the client refuses to create a
 * duplicate for an idempotent key, so a double callback appends nothing the second time.
 * Returns the stored event, or null when the key already exists.
 */
export async function appendEvent<T extends EventType>(
  type: T,
  payload: PayloadOf<T>,
  options: AppendOptions,
): Promise<AnyEvent | null> {
  const ts = options.now ?? Date.now()
  const event = createEvent(type, payload, {
    childId: options.childId,
    deviceId: await deviceId(),
    ts,
    localDate: localDateOf(ts),
    ...(options.reviewMode ? { reviewMode: true } : {}),
  }) as AnyEvent

  const parsed = safeParseEvent(event)
  if (!parsed.ok) {
    logger.error(`refused to append a ${type} that does not match the schema`, parsed.issue)
    return null
  }

  const key = idempotencyKey(event)
  if (key !== null && (await hasKey(options.childId, key))) {
    logger.debug(`skipped a duplicate ${type}`, key)
    return null
  }

  await db.events.put({ ...event, synced: 0 })
  return event
}

async function hasKey(childId: string, key: string): Promise<boolean> {
  const rows = await db.events.where('childId').equals(childId).toArray()
  return rows.some((row) => idempotencyKey(row) === key)
}

/**
 * Every event of a child in fold order. A corrupt row is logged and skipped so one bad row can
 * never take the whole fold down (ARCHITECTURE §4.3).
 */
export async function loadEvents(childId: string): Promise<AnyEvent[]> {
  const rows = await db.events.where('childId').equals(childId).toArray()
  const events: AnyEvent[] = []
  for (const row of rows) {
    const parsed = safeParseEvent(stripStorageFields(row))
    if (parsed.ok) {
      events.push(parsed.event)
      continue
    }
    logger.warn(`skipped a corrupt event row ${row.id}`, parsed.issue)
  }
  return events
}

export async function unsyncedEvents(childId: string, limit: number): Promise<AnyEvent[]> {
  const rows = await db.events.where('childId').equals(childId).toArray()
  return rows
    .filter((row) => row.synced === 0)
    .sort((a, b) => a.ts - b.ts)
    .slice(0, limit)
    .map(stripStorageFields)
}

export async function markSynced(accepted: { id: string; seq: number }[]): Promise<void> {
  await db.transaction('rw', db.events, async () => {
    for (const row of accepted) {
      const existing = await db.events.get(row.id)
      if (!existing) continue
      await db.events.put({ ...existing, seq: row.seq, synced: 1 })
    }
  })
}

/** Inserts events pulled from the server, keeping any row the device already has. */
export async function insertPulled(events: AnyEvent[]): Promise<number> {
  let inserted = 0
  await db.transaction('rw', db.events, async () => {
    for (const event of events) {
      const existing = await db.events.get(event.id)
      if (existing) {
        if (existing.seq === event.seq && existing.synced === 1) continue
        await db.events.put({ ...existing, seq: event.seq, synced: 1 })
        continue
      }
      await db.events.put({ ...event, synced: 1 })
      inserted += 1
    }
  })
  return inserted
}

export async function clearChild(childId: string): Promise<void> {
  try {
    await db.events.where('childId').equals(childId).delete()
  } catch (error) {
    logger.error('could not clear the child event log', reason(error))
    throw error
  }
}

function stripStorageFields(row: StoredEvent | AnyEvent): AnyEvent {
  const { synced: _synced, ...event } = row as StoredEvent
  return event as AnyEvent
}
